import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import { Trophy, BarChart3, Loader2, Sparkles, Calculator, ArrowRight, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const MockTests = () => {
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [score, setScore] = useState('');
  const [total, setTotal] = useState('300');
  const [exam, setExam] = useState('JEE');
  const [prediction, setPrediction] = useState(null);

  const fetchStatus = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/mock-test/status`);
      setStatus(data);
    } catch (e) { console.error('Failed to load mock pass status:', e); }
    setLoading(false);
  };
  useEffect(() => { fetchStatus(); }, []);

  const handleBuy = async () => {
    setPaying(true);
    try {
      const ok = await loadRazorpayScript();
      if (!ok) { toast.error('Razorpay failed to load'); setPaying(false); return; }
      const { data } = await axios.post(`${API_URL}/api/mock-test/purchase`);
      const options = {
        key: data.key_id, order_id: data.order_id, amount: data.amount, currency: 'INR',
        name: data.name, description: data.description,
        prefill: { name: data.user.name, email: data.user.email },
        theme: { color: '#0EA5E9' },
        handler: async (resp) => {
          try {
            await axios.post(`${API_URL}/api/mock-test/verify`, {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success('Mock Test Pass activated! 50 mocks unlocked.');
            await refreshUser();
            await fetchStatus();
          } catch (err) {
            toast.error(err.response?.data?.detail || 'Verification failed');
          }
        },
        modal: { ondismiss: () => setPaying(false) },
      };
      new window.Razorpay(options).open();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
      setPaying(false);
    }
  };

  const handlePredict = async () => {
    if (!score || !total) { toast.error('Enter score and total'); return; }
    try {
      const { data } = await axios.post(`${API_URL}/api/mock-test/predict-rank`, {
        score: parseFloat(score), total: parseFloat(total), exam,
      });
      setPrediction(data);
    } catch (err) {
      toast.error('Prediction failed');
    }
  };

  if (loading) return <div className="min-h-screen"><Header /><div className="flex items-center justify-center py-32"><Loader2 className="w-10 h-10 text-[#0EA5E9] animate-spin" /></div></div>;

  return (
    <div className="min-h-screen" data-testid="mock-tests-page">
      <Header />
      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-100 text-blue-700 text-xs font-bold uppercase tracking-wider mb-4">
            <Trophy className="w-4 h-4" /> Mock Test Pass
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-[#111827] mb-3" style={{ fontFamily: 'Fredoka, sans-serif' }} data-testid="mock-tests-title">
            50 JEE/NEET mocks · ₹999
          </h1>
          <p className="text-base text-[#6B7280] max-w-2xl mx-auto">
            Full-length, exam-pattern mocks with AI-powered All-India rank prediction. Valid for 180 days.
          </p>
        </div>

        {/* Pass status / purchase card */}
        <div className="rounded-3xl p-7 bg-gradient-to-br from-[#0EA5E9] to-[#0284C7] text-white shadow-2xl mb-10 relative overflow-hidden" data-testid="mock-pass-card">
          <div className="absolute -top-12 -right-12 w-48 h-48 bg-amber-400/30 rounded-full blur-3xl" />
          <div className="relative">
            {status?.active ? (
              <div data-testid="mock-pass-active">
                <p className="text-xs uppercase tracking-wider opacity-80 mb-2">Active Pass</p>
                <p className="text-3xl font-black mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  {status.is_pro ? 'Unlimited (Pro)' : `${status.mocks_remaining} / ${status.mocks_total} mocks remaining`}
                </p>
                {status.expires_at && <p className="text-sm opacity-90">Expires {new Date(status.expires_at).toLocaleDateString()}</p>}
                <button
                  onClick={() => navigate('/quiz-pools')}
                  className="mt-5 inline-flex items-center gap-2 px-6 py-3 bg-amber-400 text-[#111827] rounded-2xl font-bold shadow-md hover:bg-amber-300 transition"
                  data-testid="mock-pass-go-to-pools"
                >
                  Pick a mock <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div data-testid="mock-pass-inactive">
                <p className="text-4xl sm:text-5xl font-black" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  ₹{status?.price_inr || 999}
                </p>
                <p className="text-sm opacity-90 mt-1 mb-5">{status?.mocks_total || 50} mocks · 180 days validity · Cancel anytime within 7 days</p>
                <ul className="space-y-1.5 mb-6">
                  {['Exam-pattern question paper', 'All-India rank prediction', 'Topic-wise analytics', 'Detailed review of every answer'].map((b, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm"><CheckCircle2 className="w-4 h-4 text-amber-300" /> {b}</li>
                  ))}
                </ul>
                <button
                  onClick={handleBuy}
                  disabled={paying}
                  className="px-7 py-4 bg-amber-400 hover:bg-amber-300 text-[#111827] rounded-2xl font-black shadow-[0_4px_0_rgb(180,83,9)] active:translate-y-1 active:shadow-none transition flex items-center gap-2 disabled:opacity-60"
                  data-testid="buy-mock-pass-button"
                >
                  {paying ? <><Loader2 className="w-5 h-5 animate-spin" /> Opening checkout…</> : <><Sparkles className="w-5 h-5" /> Buy Mock Test Pass</>}
                </button>
                <p className="mt-3 text-xs opacity-75">Or <Link to="/upgrade" className="underline font-bold">go Pro at ₹299/mo</Link> for unlimited mocks + everything else.</p>
              </div>
            )}
          </div>
        </div>

        {/* Free Rank Predictor */}
        <div className="rounded-3xl p-6 bg-white border border-slate-100 shadow-sm" data-testid="rank-predictor-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center"><BarChart3 className="w-5 h-5 text-emerald-600" /></div>
            <div>
              <h3 className="text-lg font-bold text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }}>Quick rank predictor (free)</h3>
              <p className="text-xs text-[#6B7280]">Enter your mock score → get an estimated AIR.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-4">
            <select value={exam} onChange={(e) => setExam(e.target.value)} className="px-3 py-2 border-2 border-[#E5E7EB] rounded-xl focus:border-[#0EA5E9] outline-none" data-testid="predictor-exam">
              <option value="JEE">JEE</option><option value="NEET">NEET</option>
            </select>
            <input type="number" value={score} onChange={(e) => setScore(e.target.value)} placeholder="Your score" className="px-3 py-2 border-2 border-[#E5E7EB] rounded-xl focus:border-[#0EA5E9] outline-none" data-testid="predictor-score" />
            <input type="number" value={total} onChange={(e) => setTotal(e.target.value)} placeholder="Total" className="px-3 py-2 border-2 border-[#E5E7EB] rounded-xl focus:border-[#0EA5E9] outline-none" data-testid="predictor-total" />
            <button onClick={handlePredict} className="px-4 py-2 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 transition" data-testid="predictor-button">
              Predict rank
            </button>
          </div>
          {prediction && (
            <div className="mt-4 p-4 rounded-xl bg-emerald-50 border border-emerald-200" data-testid="prediction-result">
              <p className="text-sm text-emerald-800">
                Score: <strong>{prediction.score}/{prediction.total} ({prediction.percentage}%)</strong> · Predicted AIR: <strong className="text-2xl text-emerald-700" style={{ fontFamily: 'Fredoka, sans-serif' }}>~{prediction.predicted_rank.toLocaleString()}</strong> out of {prediction.total_candidates.toLocaleString()} candidates.
              </p>
              <Link to="/college-predictor" className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-emerald-700 hover:underline" data-testid="prediction-to-college">
                <Calculator className="w-4 h-4" /> See which colleges this rank can get you →
              </Link>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default MockTests;
